import { Table, Select, DatePicker, Button, Space, Tag, message, Progress, Input } from 'antd'
import { SendOutlined, FilterOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker

function Bills() {
  const { t } = useTranslation()
  const [bills, setBills] = useState([])
  const [billTypes, setBillTypes] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedType, setSelectedType] = useState(null)
  // فترة افتراضية: تاريخ اليوم (من وإلى نفس اليوم) — تظهر مُعبَّأة
  // مسبقاً في الفلتر عند فتح الصفحة، لكن دون جلب تلقائي؛ يبقى
  // الضغط على زر "بحث" هو ما يُنفِّذ الاستعلام كالمعتاد
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()])
  const [sentStatus, setSentStatus] = useState('all')
  const [syncStatus, setSyncStatus] = useState('all')
  const [billNoSearch, setBillNoSearch] = useState('')
  const [custNameSearch, setCustNameSearch] = useState('')
  const [selectedRows, setSelectedRows] = useState([])
  const [sending, setSending] = useState(false)
  const [sendProgress, setSendProgress] = useState({ done: 0, total: 0 })

  const token = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    fetchBillTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchBillTypes = async () => {
    try {
      const res = await axios.get('/api/settings/bill-types', { headers })
      setBillTypes(res.data)
    } catch (err) {
      message.error(t('bills.fetchBillTypesError'))
    }
  }

  const fetchBills = async () => {
    if (billNoSearch.trim() && !selectedType) {
      message.warning(t('bills.searchByBillNoWarning'))
    }
    setLoading(true)
    try {
      const params = {}
      if (selectedType) params.type_guid = selectedType
      if (dateRange) {
        params.date_from = dayjs(dateRange[0]).format('YYYY-MM-DD')
        params.date_to = dayjs(dateRange[1]).format('YYYY-MM-DD')
      }
      if (sentStatus !== 'all') params.sent_status = sentStatus
      if (syncStatus !== 'all') params.sync_status = syncStatus
      if (billNoSearch.trim()) params.bill_no = billNoSearch.trim()
      if (custNameSearch.trim()) params.cust_name = custNameSearch.trim()

      const res = await axios.get('/api/settings/bills', { headers, params })
      setBills(res.data)
    } catch (err) {
      message.error(t('bills.fetchBillsError'))
    }
    setLoading(false)
  }

  // إرسال فاتورة واحدة عبر واتساب — نستخدم Guid لتحديد الفاتورة بدقة
  // (BillNo قد يتكرر بين فواتير مختلفة في نفس نظام ERP)
  const sendOneBill = async (record) => {
    await axios.post(
      '/api/whatsapp/send-bill',
      { billGuid: record.Guid, billNo: record.BillNo },
      { headers }
    )
  }

  // إرسال كل الفواتير المحددة (زر الإرسال الجماعي)
  const sendSelectedBills = async () => {
    if (selectedRows.length === 0) return

    setSending(true)
    setSendProgress({ done: 0, total: selectedRows.length })

    let successCount = 0
    let failCount = 0

    for (const row of selectedRows) {
      try {
        await sendOneBill(row)
        successCount++
      } catch (err) {
        failCount++
        console.error(`${row.BillNo}:`, err.response?.data?.error || err.message)
      }
      setSendProgress((prev) => ({ ...prev, done: prev.done + 1 }))
    }

    setSending(false)

    if (failCount === 0) {
      message.success(t('bills.sendAllSuccess', { count: successCount }))
    } else {
      message.warning(t('bills.sendPartial', { success: successCount, fail: failCount }))
    }

    setSelectedRows([])
  }

  // إرسال فاتورة واحدة من صف الجدول مباشرة
  const sendSingleBillFromRow = async (record) => {
    const key = `send-${record.Guid || record.BillNo}`
    message.loading({ content: t('bills.sendOneLoading', { billNo: record.BillNo }), key })
    try {
      await sendOneBill(record)
      message.success({ content: t('bills.sendOneSuccess', { billNo: record.BillNo }), key })
    } catch (err) {
      message.error({
        content: err.response?.data?.error || t('bills.sendOneFail', { billNo: record.BillNo }),
        key
      })
    }
  }

  const columns = [
    { title: t('bills.columnBillType'), dataIndex: 'BtName', key: 'BtName',
      render: name => <Tag color="blue">{name}</Tag> },
    { title: t('bills.columnBillNo'), dataIndex: 'BillNo', key: 'BillNo', width: 120 },
    { title: t('bills.columnDate'), dataIndex: 'Date', key: 'Date',
      render: d => dayjs(d).format('YYYY-MM-DD'), width: 110 },
    { title: t('bills.columnCustCode'), dataIndex: 'AccCustCode', key: 'AccCustCode', width: 100 },
    { title: t('bills.columnAccount'), dataIndex: 'AccCustName', key: 'AccCustName' },
    { title: t('bills.columnCustomer'), dataIndex: 'CustName', key: 'CustName' },
    { title: t('bills.columnBeforeTax'), dataIndex: 'TotalBeforeTax', key: 'TotalBeforeTax',
      render: v => v?.toLocaleString(), width: 120 },
    { title: t('bills.columnTax'), dataIndex: 'AddTax', key: 'AddTax',
      render: v => v?.toLocaleString(), width: 100 },
    { title: t('bills.columnTotal'), dataIndex: 'FinalAfterTax', key: 'FinalAfterTax',
      render: v => <strong>{v?.toLocaleString()}</strong>, width: 120 },
    { title: t('bills.columnMobile'), dataIndex: 'Mobile', key: 'Mobile', width: 120 },
    {
      title: t('bills.columnSentStatus'),
      dataIndex: 'sent',
      key: 'sent',
      width: 110,
      render: (sent) => sent
        ? <Tag color="green">{t('bills.sent')}</Tag>
        : <Tag color="default">{t('bills.notSent')}</Tag>
    },
    {
      title: t('bills.columnSyncStatus'),
      dataIndex: 'synced',
      key: 'synced',
      width: 110,
      render: (synced) => synced
        ? <Tag color="blue">{t('bills.synced')}</Tag>
        : <Tag color="default">{t('bills.notSynced')}</Tag>
    },
    {
      title: t('bills.columnSend'),
      key: 'send',
      width: 110,
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          icon={<SendOutlined />}
          style={{ background: '#25D366', borderColor: '#25D366' }}
          disabled={!record.Mobile || sending}
          onClick={() => sendSingleBillFromRow(record)}
        >
          {t('bills.sendBtn')}
        </Button>
      )
    },
  ]

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          placeholder={t('bills.searchPlaceholder')}
          style={{ width: 180 }}
          allowClear
          value={billNoSearch}
          onChange={(e) => setBillNoSearch(e.target.value)}
          onPressEnter={fetchBills}
        />
        <Input
          placeholder={t('bills.searchByCustNamePlaceholder')}
          style={{ width: 200 }}
          allowClear
          value={custNameSearch}
          onChange={(e) => setCustNameSearch(e.target.value)}
          onPressEnter={fetchBills}
        />
        <Select
          placeholder={t('bills.chooseTypePlaceholder')}
          style={{ width: 200 }}
          allowClear
          onChange={setSelectedType}
          options={billTypes.map(bt => ({ label: bt.name, value: bt.guid }))}
        />
        <RangePicker value={dateRange} onChange={setDateRange} />
        <Select
          value={sentStatus}
          style={{ width: 170 }}
          onChange={setSentStatus}
          options={[
            { label: t('bills.allBills'), value: 'all' },
            { label: t('bills.notSentYet'), value: 'not_sent' },
            { label: t('bills.sentAlready'), value: 'sent' },
          ]}
        />
        <Select
          value={syncStatus}
          style={{ width: 170 }}
          onChange={setSyncStatus}
          options={[
            { label: t('bills.allBills'), value: 'all' },
            { label: t('bills.syncedOnly'), value: 'synced' },
            { label: t('bills.notSyncedOnly'), value: 'not_synced' },
          ]}
        />
        <Button
          type="primary"
          icon={<FilterOutlined />}
          onClick={fetchBills}
        >
          {t('bills.searchBtn')}
        </Button>
        {selectedRows.length > 0 && (
          <Button
            type="primary"
            icon={<SendOutlined />}
            style={{ background: '#25D366' }}
            loading={sending}
            onClick={sendSelectedBills}
          >
            {t('bills.sendWhatsappBtn', { count: selectedRows.length })}
          </Button>
        )}
      </div>

      {sending && (
        <div style={{ marginBottom: 16, maxWidth: 400 }}>
          <Progress
            percent={Math.round((sendProgress.done / sendProgress.total) * 100)}
            status="active"
          />
          <span style={{ fontSize: 13, color: '#888' }}>
            {t('bills.sendingLabel', { done: sendProgress.done, total: sendProgress.total })}
          </span>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={bills}
        loading={loading}
        rowKey={(r) => r.Guid || r.BillNo}
        rowSelection={{
          selectedRowKeys: selectedRows.map(r => r.Guid || r.BillNo),
          onChange: (_, rows) => setSelectedRows(rows)
        }}
        scroll={{ x: 1200 }}
        summary={() => bills.length > 0 && (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>{t('bills.totalRow', { count: bills.length })}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}>
                <strong>
                  {bills.reduce((s, r) => s + (r.TotalBeforeTax || 0), 0).toLocaleString()}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}>
                <strong>
                  {bills.reduce((s, r) => s + (r.AddTax || 0), 0).toLocaleString()}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7}>
                <strong>
                  {bills.reduce((s, r) => s + (r.FinalAfterTax || 0), 0).toLocaleString()}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} />
              <Table.Summary.Cell index={9} />
              <Table.Summary.Cell index={10} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  )
}

export default Bills
